import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { WORDMARK } from '../../data/registration';
export interface PhotoCaption {
  eyebrow: string;
  line: string;
  names?: string[];
  stat?: {
    value: string;
    label: string;
  };
  footnote?: string;
}
interface PhotoSideProps {
  image: {
    src: string;
    alt: string;
  };
  caption: PhotoCaption;
}

/**
 * The archive side of the flow. A single photograph fills the column and
 * changes as the member moves; the caption beneath it escalates from the place
 * to the people, so what the portal gives back grows as they invest.
 */
export function PhotoSide({
  image,
  caption
}: PhotoSideProps) {
  return <div className="relative h-full w-full overflow-hidden bg-ink" data-id="element-417" data-mp="2j3qhvqy42m6g">
      <AnimatePresence initial={false} data-id="element-418" data-mp="2nzfd5i33lgwy">
        <motion.img key={image.src} src={image.src} alt={image.alt} initial={{
        opacity: 0,
        scale: 1.03
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="absolute inset-0 h-full w-full object-cover" data-id="element-419" data-mp="2o2l3t1rdkqus" />
      </AnimatePresence>

      <div aria-hidden="true" className="absolute inset-0 bg-ink opacity-60" data-id="element-420" data-mp="20hy3a21pztwu" />

      <div className="relative flex h-full flex-col justify-between p-6 sm:p-8" data-id="element-421" data-mp="1vhadtkt53l4q">
        <div className="flex items-center gap-3" data-id="element-422" data-mp="1qiqp5cn5vv8o">
          <span className="text-heading font-semibold tracking-tight text-on-ink" data-id="element-423" data-mp="1qk1uvi063d2f">Portal</span>
          <span aria-hidden="true" className="h-4 w-px bg-on-ink opacity-40" data-id="element-424" data-mp="2o7aaiwdhlrwi" />
          <span className="flex items-center gap-2 text-eyebrow-caption font-medium uppercase text-on-ink" data-id="element-425" data-mp="2j74p3xxt91mv">
            <span className="opacity-70" data-id="element-426" data-mp="2edjp60cnpxl1">Run by alumni volunteers</span>
            <span className="sr-only" data-id="element-427" data-mp="29frytarnujiy">{WORDMARK}</span>
          </span>
        </div>

        <AnimatePresence mode="wait" data-id="element-428" data-mp="36wz16dab2m62">
          <motion.figure key={caption.line} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.28,
          ease: 'easeOut'
        }} className="flex max-w-md flex-col gap-3" data-id="element-429" data-mp="36y0d0jz5bcou">
            <span className="flex items-center gap-2 text-eyebrow-caption font-semibold uppercase text-on-ink" data-id="element-430" data-mp="158kr23nunzhu">
              <span aria-hidden="true" className="h-px w-6 bg-highlight" data-id="element-431" data-mp="vgyi6dyzici9" />
              {caption.eyebrow}
            </span>

            <figcaption className="text-section-heading font-medium text-on-ink" data-id="element-432" data-mp="hgxhayipz52s">
              {caption.line}
            </figcaption>

            {caption.names && caption.names.length > 0 ? <ul className="flex flex-wrap gap-2" data-id="element-433" data-mp="7jsucwh1v5kx">
                {caption.names.map(name => <li key={name} className="rounded-pill bg-on-ink px-3 py-1 text-small text-ink" data-id="element-434" data-mp="chaupyh04chb">
                    {name}
                  </li>)}
              </ul> : null}

            {caption.stat ? <p className="text-small text-on-ink opacity-80" data-id="element-435" data-mp="3rdcuxbn5nooh">
                <span className="text-body font-semibold" data-id="element-436" data-mp="3rbtg7ph5dlot">{caption.stat.value}</span>{' '}
                {caption.stat.label}
              </p> : null}

            {caption.footnote ? <p className="text-small italic text-on-ink opacity-80" data-id="element-437" data-mp="7uxw8ppyzbl">{caption.footnote}</p> : null}
          </motion.figure>
        </AnimatePresence>
      </div>
    </div>;
}